/* eslint-disable react/jsx-key */
import {
  Button,
  Calendar,
  ConfigProvider,
  Form,
  Modal,
  Typography,
  notification,
  Flex,
} from "antd";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMode } from "../../organisms";
import axios from "axios";
import Cookies from "js-cookie";
import { axiosInstance } from "../../../service/client";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import "dayjs/locale/pl";
import locale from "antd/lib/locale/pl_PL";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";

dayjs.locale("pl");
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

dayjs.extend(duration);
dayjs.extend(isBetween);

const HOURS_START = 6;
const HOURS_END = 22;
const DATE_FORMAT = "YYYY-MM-DD HH:mm";

function generateDays(start: Dayjs, end: Dayjs, format: string = DATE_FORMAT) {
  const days = [];
  let current = dayjs(start, format).startOf("day");
  const last = dayjs(end, format).startOf("day");
  while (current.isBefore(last) || current.isSame(last)) {
    days.push(current);
    current = current.add(1, "day");
  }
  return days;
}

function isDateInRanges(date: Dayjs, start: string, end: string) {
  const startDayjs = dayjs(start, { format: "YYYY-MM-DD" });
  const endDayjs = dayjs(end, { format: "YYYY-MM-DD" });
  return (
    date.isSameOrAfter(startDayjs, "day") &&
    date.isSameOrBefore(endDayjs, "day")
  );
}

// Funkcja hashująca (prosta i szybka)
function hashId(id: number) {
  let h = id;
  h = ((h >> 16) ^ h) * 0x45d9f3b;
  h = ((h >> 16) ^ h) * 0x45d9f3b;
  h = (h >> 16) ^ h;
  return h >>> 0; // bez znaku
}

// Generator koloru w HSL → HEX
function idToColor(id: number, opacity: string = "0.3") {
  const hash = hashId(id);

  // Hue w zakresie 0-360 (różne odcienie)
  const hue = hash % 360;

  // Saturation i Lightness w bezpiecznych zakresach (żeby były żywe kolory)
  const saturation = 60 + (hash % 30); // 60–90%
  const lightness = 45 + (hash % 20); // 45–65%

  return `hsl(${hue}, ${saturation}%, ${lightness}%, ${opacity})`;
}
interface EventItem {
  id: string;
  item: { start: string; end: string; content: Record<string, string> };
}

interface TimelineProps {
  events: EventItem[];
  currentDate?: Dayjs;
}
// Stałe do obliczeń
const START_HOUR = 6;
const END_HOUR = 22;
const TOTAL_MINUTES = (END_HOUR - START_HOUR) * 25;

// Funkcja pomocnicza do konwersji czasu na minuty
const timeToMinutes = (time: string): number => {
  const [hour, minute] = time.split(":").map(Number);
  return (hour - START_HOUR) * 60 + minute;
};
// Funkcja obliczająca pozycję wiersza w siatce (grid-row)
const getGridRowPosition = (start: string, end: string) => {
  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  const startMinutesSinceStart = (startHour - START_HOUR) * 60 + startMinute;
  const endMinutesSinceStart = (endHour - START_HOUR) * 60 + endMinute;

  // Sprawdzamy, czy wydarzenie trwa do końca dnia (lub dłużej)
  const isAllDay = endHour >= END_HOUR;

  const finalEndMinutes = isAllDay ? TOTAL_MINUTES : endMinutesSinceStart;

  return {
    gridRowStart: startMinutesSinceStart + 1,
    gridRowEnd: finalEndMinutes + 1,
  };
};

// Funkcja do grupowania nachodzących na siebie wydarzeń
const getCollisions = (events: EventItem[]) => {
  const sortedEvents = [...events].sort(
    (a, b) => timeToMinutes(a.item.start) - timeToMinutes(b.item.start)
  );

  const tracks: { track: EventItem[]; maxColumns: number }[] = [];

  sortedEvents.forEach((event) => {
    let placed = false;
    for (const trackData of tracks) {
      // Sprawdzamy, czy w danym torze jest wolne miejsce
      const lastEventInTrack = trackData.track[trackData.track.length - 1];
      if (
        !lastEventInTrack ||
        timeToMinutes(event.item.start) >=
          timeToMinutes(lastEventInTrack.item.end)
      ) {
        trackData.track.push(event);
        placed = true;
        break;
      }
    }

    if (!placed) {
      tracks.push({ track: [event], maxColumns: 1 });
    }
  });

  return tracks.map((trackData) => trackData.track);
};

// Funkcja do przypisywania kolumn i obliczania pozycji
const distributeEventsInColumns = (events: EventItem[]) => {
  const eventData = events.map((event) => ({
    ...event,
    startMinutes: timeToMinutes(event.item.start),
    endMinutes: timeToMinutes(event.item.end),
    column: 0,
    width: 100,
  }));

  const tracks: { end: number; column: number }[] = [];

  eventData.forEach((event) => {
    let placedInTrack = false;
    for (let i = 0; i < tracks.length; i++) {
      if (event.startMinutes >= tracks[i].end) {
        event.column = tracks[i].column;
        tracks[i].end = event.endMinutes;
        placedInTrack = true;
        break;
      }
    }

    if (!placedInTrack) {
      event.column = tracks.length;
      tracks.push({ end: event.endMinutes, column: tracks.length });
    }
  });

  const maxColumns = tracks.length > 0 ? tracks.length : 1;

  eventData.forEach((event) => {
    event.width = 100 / maxColumns;
    event.column = event.column + 1;
  });

  return { events: eventData, maxColumns };
};

const SimpleDailyTimeline: React.FC<TimelineProps> = ({ events }) => {
  const { events: processedEvents, maxColumns } = useMemo(
    () => distributeEventsInColumns(events),
    [events]
  );
  return (
    <div style={styles.timelineContainer}>
      <div
        style={{
          flexGrow: 1,
          // borderLeft: "1px solid #e0e0e0",
          position: "relative",
          display: "grid",
          minHeight: `50px`,
        }}
      >
        {processedEvents.map((e: any, index) => {
          const content = e.item.content;
          const width = e.width;
          return (
            <div
              style={{
                margin: "2px",
                width: `calc(${width}% - 2px)`,
                background: idToColor(e.id),
                borderRadius: "4px",
                position: "absolute",
                left: `${index * width}%`,
                cursor: "pointer",
                // whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {Object.values(content).map((el: any) => (
                <div style={{ padding: "5px" }}>
                  <span>{el}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Style
const styles: Record<string, React.CSSProperties> = {
  timelineContainer: {
    display: "flex",
    maxWidth: "600px",
    // border: "1px solid #e0e0e0",
    borderRadius: "8px",
    // boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    fontFamily: "sans-serif",
    // paddingTop: "10px",
  },
  hourLabelsColumn: {
    flexShrink: 0,
    width: "34px",
    position: "relative",
  },
  hourLabelItem: {
    position: "absolute",
    width: "100%",
    textAlign: "right",
    // transform: "translateY(-50%)",
  },
  hourLabelText: {
    paddingRight: "10px",
    fontSize: "0.6rem",
    color: "#888",
  },
  eventsGrid: {
    flexGrow: 1,
    borderLeft: "1px solid #e0e0e0",
    position: "relative",
    display: "grid",
    minHeight: `${TOTAL_MINUTES}px`,
  },
  hourLine: {
    position: "absolute",
    width: "100%",
    borderTop: "1px dashed #e0e0e0",
    left: 0,
    transform: "translateY(-50%)",
  },
  eventItem: {
    backgroundColor: "#e6f7ff",
    borderLeft: "4px solid #1890ff",
    borderRadius: "4px",
    padding: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    zIndex: 10,
    cursor: "pointer",
    position: "relative",
  },
  eventContent: {
    overflow: "hidden",
  },
  eventTitle: {
    margin: "0 0 5px",
    fontSize: "1rem",
    color: "#333",
  },
  eventDescription: {
    margin: "0",
    fontSize: "0.75rem",
    color: "#666",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  eventTime: {
    fontSize: "0.7rem",
    color: "#1890ff",
    fontWeight: "bold",
    marginTop: "5px",
  },
};

export const Redirect: React.FC<{ control: any; pre_name?: any }> = ({
  control,
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const DailyTimeline: React.FC<TimelineProps> = ({ events, currentDate }) => {
    const hours = Array.from({ length: 17 }, (_, i) => i + START_HOUR);

    const { events: processedEvents, maxColumns } = useMemo(
      () => distributeEventsInColumns(events),
      [events]
    );
    return (
      <div style={styles.timelineContainer}>
        <div style={styles.hourLabelsColumn}>
          {hours.map((hour, index) => (
            <div
              key={hour}
              style={{
                ...styles.hourLabelItem,
                top: `${index * 22}px`,
                borderBottom: "1px dashed rgb(224, 224, 224)",
              }}
            >
              <span style={styles.hourLabelText}>{`${hour}:00`}</span>
            </div>
          ))}
        </div>

        <div
          style={{
            ...styles.eventsGrid,
          }}
        >
          <div
            style={
              {
                // marginTop: "22px",
              }
            }
          >
            {hours.map((hour, index) => (
              <div
                key={hour}
                style={{
                  ...styles.hourLabelItem,
                  top: `${index * 22}px`,
                  borderBottom: "1px dashed rgb(224, 224, 224)",
                  width: "100%",
                  height: `${22}px`,
                }}
              ></div>
            ))}
          </div>
          {processedEvents.map((e: any, index) => {
            const current = dayjs(currentDate).format("YYYY-MM-DD");
            const start = dayjs(e.item.start).format("YYYY-MM-DD");
            const end = dayjs(e.item.end).format("YYYY-MM-DD");
            let style: any = {
              height: "10px",
            };

            if (start === end) {
              const endWithTime = dayjs(
                dayjs(e.item.end, "YYYY-MM-DD HH-mm").format("HH-mm"),
                "HH-mm"
              );
              const startWithTime = dayjs(
                dayjs(e.item.start, "YYYY-MM-DD HH-mm").format("HH-mm"),
                "HH-mm"
              );
              const diffMs = endWithTime.diff(startWithTime); // różnica w milisekundach
              const diffHours = dayjs.duration(diffMs).asHours(); // w godzinach (np. 5.75)

              const startDay = dayjs("06:00", "HH-mm");
              const diffMs2 = startWithTime.diff(startDay); // różnica w milisekundach
              const diffHours2 = dayjs.duration(diffMs2).asHours(); // w godzinach (np. 5.75)
              style = {
                height: `${22 * diffHours}px`,
                maxHeight: `${22 * diffHours}px`,
                top: `${22 * diffHours2}px`,
              };
            } else if (current === start) {
              const startWithTime = dayjs(
                dayjs(e.item.start, "YYYY-MM-DD HH-mm").format("HH-mm"),
                "HH-mm"
              );
              const startDay = dayjs("06:00", "HH-mm");
              const diffMs = startWithTime.diff(startDay); // różnica w milisekundach
              const diffHours = dayjs.duration(diffMs).asHours(); // w godzinach (np. 5.75)
              style = {
                height: `${22 * (17 - diffHours)}px`,
                maxHeight: `${22 * (17 - diffHours)}px`,
                top: `${22 * diffHours}px`,
              };
            } else if (current === end) {
              const endWithTime = dayjs(
                dayjs(e.item.end, "YYYY-MM-DD HH-mm").format("HH-mm"),
                "HH-mm"
              );
              const endDay = dayjs("22:00", "HH-mm");
              const diffMs = endDay.diff(endWithTime); // różnica w milisekundach
              const diffHours = dayjs.duration(diffMs).asHours(); // w godzinach (np. 5.75)

              console.log(diffHours, endWithTime, endDay); // 5.75
              style = {
                height: `${22 * (17 - diffHours)}px`,
                maxHeight:  `${22 * (17 - diffHours)}px`,
                top: 0,
              };
            } else {
              style = {
                height: `${22 * 17}px`,
                maxHeight: `${22 * 17}px`,
                top: 0,
              };
            }

            const content = e.item.content;
            const width = e.width;
            return (
              <div
                style={{
                  margin: "2px",
                  width: `calc(${width}% - 2px)`,
                  background: idToColor(e.id),
                  borderRadius: "4px",
                  position: "absolute",
                  left: `${index * width}%`,
                  cursor: "pointer",
                  // whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  ...style,
                }}
                onClick={() => {
                  navigate({
                    search: `${e.item.id_filter_uuid}=${e.id}`,
                  });
                  Modal.destroyAll();
                  setIsModalOpen(false);
                }}
              >
                {Object.values(content).map((el: any) => (
                  <div style={{ padding: "5px" }}>
                    <span>{el}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const { mode, global_actions } = useMode();

  const location = useLocation();
  const url = control?.value.with_search_parameters
    ? control.value.url + location.search
    : control.value.url;
  const [loading, setLoading] = useState(false);
  const click = () => {
    setLoading(true);
    const accessToken = Cookies.get("accessToken");
    axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: "arraybuffer",
      })
      .then((response) => {
        const type = response.headers?.["content-type"] ?? "application/pdf";
        const blob = new Blob([response.data], { type: type });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setLoading(false);
      })
      .catch(() => {
        notification.error({
          message: "Pobierania nowych danych",
          style: {
            background: "#ff4d4f30",
          },
        });
        setLoading(false);
      });
  };
  const [dataArray, setDataArray] = useState([]);
  const [loading2, setLoading2] = useState(false);

  const getCalendarData = async (url: string) => {
    setLoading2(true);
    const response = await axiosInstance.get(url);
    setDataArray(response.data);
    setLoading2(false);
    setIsModalOpen(true);
  };

  const cellRender = (date: Dayjs) => {
    const elements: any = dataArray.filter((e: any) =>
      isDateInRanges(date, e.item.start, e.item.end)
    );
    if (elements && elements.length > 0)
      return <SimpleDailyTimeline events={elements} />;
  };

  if (control.value.redirect_type === "calendar") {
    return (
      <>
        <Form.Item
          style={{ margin: 0, marginTop: global_actions ? "" : "30px" }}
        >
          <Button
            type={control.type ?? "dashed"}
            onClick={() => {
              getCalendarData(control.value.fetch_url);
            }}
            disabled={mode === "edit" || !control.writeable}
            loading={loading2}
          >
            {control?.field_name}
          </Button>
        </Form.Item>
        <Modal
          title="Kalendarz zleceń"
          open={isModalOpen}
          destroyOnClose
          width={"100%"}
          // onOk={() => toggleModal(0, false)}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          style={{
            top: "10px",
          }}
        >
          <ConfigProvider locale={locale}>
            <Calendar
              onSelect={(date2) => {
                Modal.info({
                  title: `Szczegóły ${dayjs(date2).format("YYYY-MM-DD")}`,
                  icon: null,
                  closable: true,
                  content: (
                    <Flex vertical gap={8}>
                      <Button
                        type="primary"
                        onClick={() => {
                          navigate({
                            search: `7ee20224-43ab-48d7-9299-0eac1c1a7805=${dayjs(
                              date2
                            ).format("YYYY-MM-DD")}`,
                          });
                          Modal.destroyAll();
                          setIsModalOpen(false);
                        }}
                      >
                        Edytuj wybrany dzien
                      </Button>
                      <Button
                        type="primary"
                        style={{
                          width: "100%",
                        }}
                        onClick={() => {
                          const elements: any = dataArray.filter((e: any) =>
                            isDateInRanges(date2, e.item.start, e.item.end)
                          );
                          if (elements && elements?.length > 0)
                            Modal.info({
                              title: `Szczegóły ${dayjs(date2).format(
                                "YYYY-MM-DD"
                              )}`,
                              icon: null,
                              closable: true,
                              content: (
                                <>
                                  <DailyTimeline
                                    events={elements}
                                    currentDate={date2}
                                  />
                                </>
                              ),
                            });
                        }}
                      >
                        Pokaz szczegoly dnia
                      </Button>
                    </Flex>
                  ),
                });
              }}
              mode="month"
              cellRender={cellRender}
            />
          </ConfigProvider>
        </Modal>
      </>
    );
  }

  if (control.value.redirect_type === "internal") {
    return (
      <>
        <Form.Item
          // tooltip={
          //   !control.writeable && mode === "edit"
          //     ? "Brak uprawnień do edycji"
          //     : control.description
          // }
          style={{ margin: 0, marginTop: global_actions ? "" : "30px" }}
        >
          <Button
            type={control.type ?? "dashed"}
            onClick={() => {
              if (control?.value?.open_in_new_tab) {
                window.open(control?.value?.url, "_blank", "noopener,noreferrer");
              } else {
                const url_data = control.value.url.split("?");
                const pathname = url_data[0];
                let search = control?.value.with_search_parameters
                  ? location.search
                  : "";
                if (url_data.length > 1) {
                  if (search.length == 0) {
                    search = "?" + url_data[1];
                  } else {
                    search = search + "&" + url_data[1];
                  }
                }
                navigate({
                  pathname: pathname,
                  search: search,
                });
              }
            }}
            disabled={mode === "edit" || !control.writeable}
          >
            {control?.field_name}
          </Button>
        </Form.Item>
      </>
    );
  }

  if (control.value.redirect_type === "external") {
    return (
      <Form.Item
        // tooltip={control?.description}
        style={{ margin: 0, marginTop: global_actions ? "" : "30px" }}
      >
        <Button
          type={control.type ?? "dashed"}
          onClick={click}
          target={control?.value?.open_in_new_tab ? "_blank" : "_self"}
          disabled={mode === "edit"}
          loading={loading}
        >
          {control?.field_name}
        </Button>
      </Form.Item>
    );
  }

  return (
    <Form.Item
      label={control?.field_name}
      // tooltip={control?.description}
      style={{ margin: 0 }}
    >
      <Typography.Text strong copyable>
        {control?.field_name}
      </Typography.Text>
    </Form.Item>
  );
};
